import { Grid, LinearProgress, Typography } from "@mui/material";
import last from "lodash/last";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProducts } from "../admin/products/useProducts";
import { isPlaneTicket } from "../admin/providers/isPlaneTicket";
import { useAuthContext } from "../auth/AuthContext";
import LabelledSwitch from "../components/labelledSwitch/LabelledSwitch";
import { useOpenInNewTab } from "../misc/useOpenInNewTab";
import { useCreateQuote } from "../quotes/useCreateQuote";
import ExpandableTasks from "../tasks/ExpandableTasks";
import CalculationVersion from "./CalculationVersion";
import CustomerSelect from "../components/customerSelect/CustomerSelect";
import AvailabilityReportDialog from "./availability/AvailabilityReportDialog";
import EditCalculationActions from "./EditCalculationActions";
import { areProductsAndCalculationCurrenciesEqual } from "./areProductsAndCalculationCurrenciesEqual";
import type {
  CalculationProduct,
  CalculationProductOption,
  CalculationVersion as CalculationVersionType,
  SavedCalculation,
  SavedCalculationProduct,
  SavedCalculationProductOption,
} from "./calculationTypes";
import CalculationVersionEdit from "./calculationVersionEdit/CalculationVersionEdit";
import CalculationVersionView from "./calculationVersionView/CalculationVersionView";
import { useCalculation } from "./useCalculation";

export function lastCalculationVersion(calculation: SavedCalculation): CalculationVersionType {
  return last(calculation.versions)!;
}

const EditCalculationPage = () => {
  const navigate = useNavigate();
  const { openInNewTab } = useOpenInNewTab();
  const { context } = useAuthContext();
  const { calculation: existingCalculation, isLoading, onDelete, onUpdate, onUpdatePrices, onOrder } = useCalculation();
  const { createQuote } = useCreateQuote();
  const { products, isLoading: areProductsLoading } = useProducts();
  const [calculationEdit, setCalculationEdit] = useState<CalculationVersionType>();
  const [versionIdx, setVersionIdx] = useState<number | undefined>(undefined);
  const [productCurrencyErrors, setProductCurrencyErrors] = useState<string[]>([]);
  const [currencyError, setCurrencyError] = useState(false);
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);

  useEffect(() => {
    setCalculationEdit(undefined);
    setVersionIdx(undefined);
  }, [existingCalculation]);

  if (isLoading || areProductsLoading) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }
  if (!existingCalculation) {
    return (
      <Grid
        container
        sx={{
          marginTop: 2,
          marginBottom: 2,
        }}
      >
        <Typography variant="h5">Taka kalkulacja nie istnieje</Typography>
      </Grid>
    );
  }
  const selectedVersionIdx = versionIdx !== undefined ? versionIdx : existingCalculation.versions.length - 1;
  const calculationVersion = calculationEdit ? calculationEdit : existingCalculation.versions[selectedVersionIdx];

  const handleChange = (change: Partial<CalculationVersionType>) => {
    setCalculationEdit((prev) => ({ ...(prev ?? calculationVersion), ...change }) as CalculationVersionType);
    setProductCurrencyErrors([]);
    setCurrencyError(false);
  };

  const handleDelete = async () => {
    await onDelete();
    navigate("/calculations");
  };

  const handleEdit = () => {
    const newIdx = existingCalculation.versions.length;
    setCalculationEdit({
      ...calculationVersion,
      versionName: undefined,
      products: calculationVersion.products.map((p) => ({
        id: p.id,
        date: p.date,
        daysShift: p.daysShift,
        options: p.options.map((o) => ({ id: o.id, quantity: o.quantity })),
      })),
    });
    setVersionIdx(newIdx);
  };

  const handleVersionChange = (newSelectedVersion: number) => {
    setCalculationEdit(undefined);
    setVersionIdx(newSelectedVersion);
  };

  const getProductCurrency = (productId: string) => products?.find((p) => p.id === productId)?.currency;

  const handleSave = async () => {
    if (!context) {
      return;
    }
    const currencyMatches = areProductsAndCalculationCurrenciesEqual(calculationVersion, products);
    if (!currencyMatches) {
      setCurrencyError(true);
      return;
    }
    const productsWithDifferentCurrency = calculationVersion.packageId
      ? []
      : calculationVersion.products
          .map((p) => p.id)
          .filter((id) => getProductCurrency(id) !== calculationVersion.currency);
    if (productsWithDifferentCurrency.length > 0) {
      setProductCurrencyErrors(productsWithDifferentCurrency);
      return;
    }
    await onUpdate(
      {
        ...calculationVersion,
        currency: calculationVersion.packageId ? undefined : calculationVersion.currency,
        margin: calculationVersion.packageId ? undefined : calculationVersion.margin,
        products: calculationVersion.products.map((p) => ({
          ...p,
          options: p.options.map((o) => ({ id: o.id, quantity: o.quantity, price: o.price ?? undefined })),
        })),
      },
      context.role,
    );
  };

  const handleOrder = async (orderFromProviders: boolean) => {
    const result = await onOrder(selectedVersionIdx + 1, orderFromProviders);
    if (result.orderId) {
      openInNewTab(`/orders/${result.orderId}`);
    }
    navigate("/calculations");
  };

  const role = context?.role;
  const versions = calculationEdit ? [...existingCalculation.versions, calculationEdit] : existingCalculation.versions;
  const editMode = !!calculationEdit;
  const versionProducts = calculationVersion.products;
  const versionProductsWithDetails = versionProducts.map((p: SavedCalculationProduct | CalculationProduct) => ({
    product: p,
    details: products.find((pr) => pr.id === p.id),
  }));
  const hasDynamicPriceProducts = versionProductsWithDetails.some(
    ({ details }) => details?.provider.useDynamicPrices ?? false,
  );
  const hasPlaneTickets = versionProductsWithDetails.some(
    ({ details }) => details?.provider.productType && isPlaneTicket(details.provider.productType),
  );
  const hasProductsForQuote = versionProductsWithDetails.some(
    ({ product, details }) =>
      (details?.provider.hasEmailRecipients ?? false) &&
      product.options.some((o: SavedCalculationProductOption | CalculationProductOption) => o.price === "ON_DEMAND"),
  );

  const isNotCustomer = role !== "CUSTOMER";
  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          marginTop: 2,
          paddingBottom: 2,
        }}
      >
        {isNotCustomer && (
          <Grid size={{ xs: 12, lg: 6 }}>
            <CustomerSelect customerId={existingCalculation.customerId} disabled showLink onChange={() => {}} />
          </Grid>
        )}
        <Grid size={{ xs: isNotCustomer ? 6 : 12, lg: isNotCustomer ? 4 : 6 }}>
          <CalculationVersion
            selectedIdx={selectedVersionIdx}
            versions={versions}
            numberOfSavedVersions={existingCalculation.versions.length}
            onVersionChange={handleVersionChange}
            onVersionNameChange={(versionName) => handleChange({ versionName: versionName ?? undefined })}
          />
        </Grid>
        {isNotCustomer && (
          <Grid size={{ xs: 6, lg: 2 }}>
            <LabelledSwitch
              label="Cena pakietowa"
              checked={existingCalculation.type === "PACKAGE"}
              disabled
              onChange={(v) => v}
            />
          </Grid>
        )}
        <Grid size={12}>
          {editMode && (
            <CalculationVersionEdit
              calculationVersion={calculationVersion}
              productCurrencyErrors={productCurrencyErrors}
              currencyError={currencyError}
              userContext={context!}
              onChange={handleChange}
            />
          )}
          {!editMode && (
            <CalculationVersionView calculationVersion={existingCalculation.versions[selectedVersionIdx]} role={role} />
          )}
        </Grid>
        <Grid
          size={12}
          container
          sx={{
            justifyContent: "flex-end",
          }}
        >
          <EditCalculationActions
            isCustomCalculation={!lastCalculationVersion(existingCalculation).packageId}
            context={context}
            editMode={editMode}
            readonly={!!existingCalculation.orderId}
            calculation={{
              id: existingCalculation.id,
              version: selectedVersionIdx + 1,
              customerId: existingCalculation.customerId,
              type: existingCalculation.type,
              hasDynamicPriceProducts,
              hasProductsForQuote,
              hasPlaneTickets,
            }}
            calculationName={calculationVersion.name}
            onDelete={handleDelete}
            onEdit={handleEdit}
            onSave={handleSave}
            saveValidationInput={
              editMode
                ? {
                    participantsNumber: calculationVersion.participantsNumber,
                    products: calculationVersion.products,
                  }
                : undefined
            }
            productDetails={products}
            onUpdatePrices={onUpdatePrices}
            onOrder={handleOrder}
            onRequestQuote={async () => {
              await createQuote({
                prepareFor: {
                  type: "CALCULATION",
                  id: existingCalculation.id,
                  calculationVersion: selectedVersionIdx + 1,
                },
              });
            }}
            onCheckAvailability={() => setAvailabilityDialogOpen(true)}
          />
        </Grid>
        {isNotCustomer && (
          <Grid size={12}>
            <ExpandableTasks
              entityType="calculations"
              entityId={existingCalculation.id}
              customerId={existingCalculation.customerId}
            />
          </Grid>
        )}
      </Grid>
      <AvailabilityReportDialog
        open={availabilityDialogOpen}
        onClose={() => setAvailabilityDialogOpen(false)}
        products={versionProductsWithDetails
          .filter(({ details }) => details?.provider.useDynamicPrices ?? false)
          .map(({ product }) => ({ productId: product.id, date: product.date }))}
        participantsNumber={
          calculationVersion.participantsNumber.numberOfPaid + calculationVersion.participantsNumber.numberOfStaff
        }
      />
    </>
  );
};

export default EditCalculationPage;
